import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { api, ApiError } from "../api/client.js";
import type { CreatedInvitation, InvitationSummary, MemberSummary, MembershipRole } from "../api/types.js";

const ROLES: readonly MembershipRole[] = ["ADMIN", "OPERATOR", "REVIEWER", "VIEWER"];

export function WorkspaceSettingsPage() {
  const { user, selectedTenantId, csrfToken, workspaces } = useAuth();
  const [invitations, setInvitations] = useState<InvitationSummary[] | null>(null);
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  // New customers default to the minimum role that already permits Generate for both Video and Image (OPERATOR); the owner can still pick another role.
  const [role, setRole] = useState<MembershipRole>("OPERATOR");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // FIX-006 (customer product-surface boundary), corrected: this whole
  // surface is platform-OWNER-only now — a workspace's own tenant `ADMIN`
  // membership role (a new workspace's creator now starts as OPERATOR,
  // see `CreateWorkspace`) is deliberately NOT sufficient authority here, even
  // for their own workspace. The backend (`workspace-routes.ts`) enforces
  // the identical `requirePlatformOwner` check on every real invitations/
  // members route, so this is real defense-in-depth, not the only gate.
  const isOwner = user?.isPlatformOwner === true;
  const workspaceName = workspaces.find((w) => w.tenantId === selectedTenantId)?.name ?? selectedTenantId;

  if (!isOwner) {
    return <Navigate to="/generate" replace />;
  }

  const loadInvitations = useCallback(async () => {
    if (selectedTenantId === null || !isOwner) {
      return;
    }
    try {
      const result = await api.get<{ invitations: InvitationSummary[] }>(`/workspaces/${encodeURIComponent(selectedTenantId)}/invitations`);
      setInvitations(result.invitations);
    } catch {
      setInvitations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, isOwner]);

  const loadMembers = useCallback(async () => {
    if (selectedTenantId === null || !isOwner) {
      return;
    }
    try {
      const result = await api.get<{ members: MemberSummary[] }>(`/workspaces/${encodeURIComponent(selectedTenantId)}/members`);
      setMembers(result.members);
    } catch {
      setMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, isOwner]);

  useEffect(() => {
    void loadInvitations();
    void loadMembers();
  }, [loadInvitations, loadMembers]);

  async function handleChangeRole(targetUserId: string, newRole: MembershipRole): Promise<void> {
    if (selectedTenantId === null) {
      return;
    }
    setMemberError(null);
    try {
      await api.patch(`/workspaces/${encodeURIComponent(selectedTenantId)}/members/${encodeURIComponent(targetUserId)}`, csrfToken, { role: newRole });
      await loadMembers();
    } catch (err) {
      setMemberError(err instanceof ApiError ? err.message : "Could not change that member's role — please try again.");
    }
  }

  async function handleRemoveMember(targetUserId: string): Promise<void> {
    if (selectedTenantId === null) {
      return;
    }
    setMemberError(null);
    try {
      await api.del(`/workspaces/${encodeURIComponent(selectedTenantId)}/members/${encodeURIComponent(targetUserId)}`, csrfToken);
      await loadMembers();
    } catch (err) {
      setMemberError(err instanceof ApiError ? err.message : "Could not remove that member — please try again.");
    }
  }

  async function handleInvite(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (selectedTenantId === null) {
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const created = await api.post<CreatedInvitation>(`/workspaces/${encodeURIComponent(selectedTenantId)}/invitations`, csrfToken, { email, role });
      // FIX (invitation email): `emailDelivered` is the backend's real, truthful signal (never assumed) — mirrors SignUpPage's own use of `emailDelivered`.
      setSuccess(created.emailDelivered ? `Invitation sent to ${email}.` : "Invitation was created, but the email could not be delivered.");
      setEmail("");
      setRole("OPERATOR");
      await loadInvitations();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the invitation — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(invitationId: string): Promise<void> {
    if (selectedTenantId === null) {
      return;
    }
    try {
      await api.del(`/workspaces/${encodeURIComponent(selectedTenantId)}/invitations/${encodeURIComponent(invitationId)}`, csrfToken);
      await loadInvitations();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke the invitation — please try again.");
    }
  }

  if (selectedTenantId === null) {
    return <div className="empty-state">No workspace selected yet. Create one from the workspace switcher above.</div>;
  }

  return (
    <div>
      <h2>Workspace: {workspaceName}</h2>
      <div className="card">
        <h3>Members</h3>
        {memberError && <div className="error-banner">{memberError}</div>}
        {members === null && <div className="muted">Loading…</div>}
        {members !== null && members.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.userId}>
                  <td>{member.email ?? member.userId}</td>
                  <td>{member.displayName ?? "—"}</td>
                  <td>
                    <select value={member.role} onChange={(event) => void handleChangeRole(member.userId, event.target.value as MembershipRole)}>
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="danger" onClick={() => void handleRemoveMember(member.userId)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="card">
        <h3>Invite a member</h3>
        {error && <div className="error-banner">{error}</div>}
        {success && <div className="success-banner">{success}</div>}
        <form onSubmit={(event) => void handleInvite(event)}>
          <div className="grid grid-2">
            <div className="field">
              <label htmlFor="inviteEmail">Email</label>
              <input id="inviteEmail" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="inviteRole">Role</label>
              <select id="inviteRole" value={role} onChange={(event) => setRole(event.target.value as MembershipRole)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? "Sending…" : "Send invitation"}
          </button>
        </form>
      </div>
      <div className="card">
        <h3>Pending invitations</h3>
        {invitations === null && <div className="muted">Loading…</div>}
        {invitations !== null && invitations.length === 0 && <div className="muted">No pending invitations.</div>}
        {invitations !== null && invitations.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <td>{invitation.email}</td>
                  <td>{invitation.role}</td>
                  <td>{new Date(invitation.expiresAt).toLocaleDateString()}</td>
                  <td>
                    <button className="danger" onClick={() => void handleRevoke(invitation.id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
